import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { db } from "@lib/firebase";
import { useAuth } from "@context/AuthContext";
import { useNotification } from "@jotrseatrc/shared-components/common/NotificationContext";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faTimesCircle,
  faCalendar,
} from "@fortawesome/free-solid-svg-icons";

const cachePesananData = (uid, data) => {
  try {
    localStorage.setItem(
      `pesanan_${uid}`,
      JSON.stringify({ data, timestamp: Date.now() })
    );
  } catch (err) {
    console.warn("Gagal menyimpan cache:", err);
  }
};

const getCachedPesananData = (uid) => {
  try {
    const cached = localStorage.getItem(`pesanan_${uid}`);
    if (!cached) return null;
    const { data, timestamp } = JSON.parse(cached);
    const cacheDuration = 5 * 60 * 1000;
    if (Date.now() - timestamp > cacheDuration) {
      localStorage.removeItem(`pesanan_${uid}`);
      return null;
    }
    if (!data || !Array.isArray(data.pesanan)) {
      console.warn("Cache tidak valid: pesanan bukan array");
      localStorage.removeItem(`pesanan_${uid}`);
      return null;
    }
    return data;
  } catch (err) {
    console.warn("Gagal mengambil cache:", err);
    localStorage.removeItem(`pesanan_${uid}`);
    return null;
  }
};

const shortenLocation = (location) => {
  if (!location || typeof location !== "string") return "-";
  const parts = location.split(" ");
  if (parts.length === 1) return location.slice(0, 8);
  if (parts[0].toLowerCase().startsWith("jakarta")) {
    return `Jkt ${parts[1]}`;
  }
  return parts[0].slice(0, 4);
};

export const PesananSaya = React.memo(() => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [pesanan, setPesanan] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.pesanan : [];
  });
  const [suratPernyataan, setSuratPernyataan] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.suratPernyataan : {};
  });
  const [membershipStatus, setMembershipStatus] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached ? cached.membershipStatus : null;
  });
  const [eventTitles, setEventTitles] = useState(() => {
    const cached = getCachedPesananData(user?.uid);
    return cached
      ? cached.eventTitles
      : { ALTI_MEMBERSHIP: "Keanggotaan ALTI" };
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [transaction, setTransaction] = useState(null);
  const [searchParams] = useSearchParams();
  const [activeMenu, setActiveMenu] = useState("Semua Pesanan");
  const [viewMode, setViewMode] = useState("pesanan");
  const [notificationShown, setNotificationShown] = useState(false);
  const isMounted = useRef(true);
  const fetchStatus = useRef({ isFetching: false });

  const orderId = searchParams.get("order_id");
  const status = searchParams.get("transaction_status");

  const menus = useMemo(
    () => [
      { name: "Semua Pesanan", icon: faCalendar },
      { name: "Menunggu", icon: faClock },
      { name: "Selesai", icon: faCheckCircle },
    ],
    []
  );

  const formatDate = useCallback((date) => {
    if (!date) return "Tidak tersedia";
    try {
      if (date && (date.seconds || date._seconds)) {
        const timestamp = date.seconds || date._seconds;
        return new Date(timestamp * 1000).toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      }
      if (date.toDate && typeof date.toDate === "function") {
        return date.toDate().toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      }
      if (typeof date === "string") {
        let parsedDate = new Date(date.replace(" at ", " "));
        if (!isNaN(parsedDate)) {
          return parsedDate.toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          });
        }
        parsedDate = new Date(date);
        if (!isNaN(parsedDate)) {
          return parsedDate.toLocaleDateString("id-ID", {
            day: "2-digit",
            month: "long",
            year: "numeric",
          });
        }
      }
      if (date instanceof Date && !isNaN(date)) {
        return date.toLocaleDateString("id-ID", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        });
      }
      console.warn("Format tanggal tidak dikenali:", date);
      return "Tidak tersedia";
    } catch (err) {
      console.error("Gagal memformat tanggal:", err, date);
      return "Tidak tersedia";
    }
  }, []);

  const fetchPesananDanSurat = useCallback(
    async (forceRefresh = false) => {
      if (!user?.uid || fetchStatus.current.isFetching) {
        if (!user?.uid) setError("Pengguna tidak terautentikasi.");
        setLoading(false);
        return;
      }

      fetchStatus.current.isFetching = true;
      try {
        setLoading(true);
        setError("");

        if (!forceRefresh) {
          const cachedData = getCachedPesananData(user.uid);
          if (cachedData && isMounted.current) {
            setPesanan(cachedData.pesanan);
            setSuratPernyataan(cachedData.suratPernyataan);
            setMembershipStatus(cachedData.membershipStatus);
            setEventTitles(
              cachedData.eventTitles || { ALTI_MEMBERSHIP: "Keanggotaan ALTI" }
            );
            setLoading(false);
            return;
          }
        }

        const pesananList = [];
        const processedOrderIds = new Set();
        let membershipData = null;
        const suratData = {};
        const eventTitlesData = {
          ALTI_MEMBERSHIP: "Keanggotaan ALTI",
          JOTR: "Jakarta Open Trail Run",
        };
        const eventTypes = {};

        try {
          const eventsSnapshot = await getDocs(collection(db, "semuaKegiatan"));
          for (const eventDoc of eventsSnapshot.docs) {
            const eventId = eventDoc.id;
            const eventData = eventDoc.data();
            eventTitlesData[eventId] = eventData.title || eventId;
            eventTypes[eventId] = eventData.type || "unknown";
          }
        } catch {}

        try {
          const memberQuery = query(
            collection(db, "daftarAnggotaAlti"),
            where("uid", "==", user.uid)
          );
          const memberSnapshot = await getDocs(memberQuery);
          if (!memberSnapshot.empty && isMounted.current) {
            const memberData = memberSnapshot.docs[0].data();
            membershipData = {
              isActive: memberData.isActive,
              activeUntil: memberData.activeUntil
                ? memberData.activeUntil
                : null,
              memberId: memberData.memberId,
              namaLengkap: memberData.namaLengkap,
              transaction_status: memberData.transaction_status,
            };
          }
        } catch {}

        try {
          const membershipQuery = query(
            collection(db, "daftarAnggotaAlti"),
            where("uid", "==", user.uid),
            where("transaction_status", "in", ["pending", "settlement"])
          );
          const membershipSnapshot = await getDocs(membershipQuery);
          membershipSnapshot.forEach((doc) => {
            const data = doc.data();
            if (!processedOrderIds.has(doc.id)) {
              pesananList.push({
                id: doc.id,
                eventId: data.membershipId || "ALTI_MEMBERSHIP",
                collectionName: "daftarAnggotaAlti",
                kategori: null,
                ...data,
              });
              processedOrderIds.add(doc.id);
            }
          });
        } catch {}

        try {
          const renewalQuery = query(
            collection(db, "membershipRenewals"),
            where("uid", "==", user.uid),
            where("transaction_status", "in", ["pending", "settlement"])
          );
          const renewalSnapshot = await getDocs(renewalQuery);
          renewalSnapshot.forEach((doc) => {
            const data = doc.data();
            if (!processedOrderIds.has(doc.id)) {
              pesananList.push({
                id: doc.id,
                eventId: "ALTI_MEMBERSHIP",
                collectionName: "membershipRenewals",
                kategori: null,
                ...data,
              });
              processedOrderIds.add(doc.id);
            }
          });
        } catch {}

        try {
          const eventsSnapshot = await getDocs(collection(db, "semuaKegiatan"));
          for (const eventDoc of eventsSnapshot.docs) {
            const eventId = eventDoc.id;
            const eventData = eventDoc.data();
            const collectionName =
              eventData.collectionName || `pendaftaran${eventId}`;
            const pesananQuery = query(
              collection(db, collectionName),
              where("uid", "==", user.uid),
              where("transaction_status", "in", ["pending", "settlement"])
            );
            const pesananSnapshot = await getDocs(pesananQuery);
            pesananSnapshot.forEach((doc) => {
              const data = doc.data();
              if (!processedOrderIds.has(doc.id)) {
                const isSeminar = eventTypes[eventId] === "seminar";
                pesananList.push({
                  id: doc.id,
                  eventId,
                  collectionName,
                  kategori: isSeminar ? null : data.kategori || null,
                  type: "event",
                  ...data,
                });
                processedOrderIds.add(doc.id);
              }
            });
          }
        } catch {}

        try {
          const shuttleQuery = query(
            collection(db, "pendaftaranShuttle"),
            where("uid", "==", user.uid),
            where("eventId", "==", "JOTR"),
            where("transaction_status", "in", ["pending", "settlement"])
          );
          const shuttleSnapshot = await getDocs(shuttleQuery);
          shuttleSnapshot.forEach((doc) => {
            const data = doc.data();
            if (!processedOrderIds.has(doc.id)) {
              pesananList.push({
                id: doc.id,
                eventId: "JOTR",
                collectionName: "pendaftaranShuttle",
                pickupPoint: data.pickupPoint || null,
                departureTime: data.departureTime || null,
                returnPoint: data.returnPoint || null,
                returnTime: data.returnTime || null,
                type: "shuttle",
                transaction_status: data.transaction_status || "pending",
                harga: data.harga || 0,
                createdAt: data.createdAt || null,
                email: data.email || null,
                telepon: data.telepon || null,
                alamat: data.alamat || null,
                namaLengkap: data.namaLengkap || null,
                snapToken: data.snapToken || null,
                orderId: data.orderId || doc.id,
              });
              processedOrderIds.add(doc.id);
            }
          });
        } catch (err) {
          console.error("Gagal mengambil data pendaftaranShuttle:", err);
        }

        try {
          const orderMappingsQuery = query(
            collection(db, "orderMappings"),
            where("uid", "==", user.uid)
          );
          const orderMappingsSnapshot = await getDocs(orderMappingsQuery);
          for (const orderDoc of orderMappingsSnapshot.docs) {
            const orderData = orderDoc.data();
            const orderId = orderData.orderId;
            const eventId = orderData.eventId;
            if (processedOrderIds.has(orderId)) continue;
            const eventDocRef = doc(db, "semuaKegiatan", eventId);
            const eventDoc = await getDoc(eventDocRef);
            const collectionName = eventDoc.exists()
              ? eventDoc.data().collectionName || `pendaftaran${eventId}`
              : `pendaftaran${eventId}`;
            const registrationDocRef = doc(db, collectionName, orderId);
            const registrationDoc = await getDoc(registrationDocRef);
            if (registrationDoc.exists()) {
              const registrationData = registrationDoc.data();
              if (
                registrationData.uid === user.uid &&
                ["pending", "settlement"].includes(
                  registrationData.transaction_status
                )
              ) {
                const isSeminar = eventTypes[eventId] === "seminar";
                pesananList.push({
                  id: registrationDoc.id,
                  eventId,
                  collectionName,
                  kategori: isSeminar
                    ? null
                    : registrationData.kategori || null,
                  type: "event",
                  ...registrationData,
                });
                processedOrderIds.add(orderId);
              }
            }
            const shuttleDocRef = doc(db, "pendaftaranShuttle", orderId);
            const shuttleDoc = await getDoc(shuttleDocRef);
            if (shuttleDoc.exists()) {
              const shuttleData = shuttleDoc.data();
              if (
                shuttleData.uid === user.uid &&
                ["pending", "settlement"].includes(
                  shuttleData.transaction_status
                ) &&
                shuttleData.eventId === "JOTR"
              ) {
                pesananList.push({
                  id: shuttleDoc.id,
                  eventId: "JOTR",
                  collectionName: "pendaftaranShuttle",
                  pickupPoint: shuttleData.pickupPoint || null,
                  departureTime: shuttleData.departureTime || null,
                  returnPoint: shuttleData.returnPoint || null,
                  returnTime: shuttleData.returnTime || null,
                  type: "shuttle",
                  transaction_status:
                    shuttleData.transaction_status || "pending",
                  harga: shuttleData.harga || 0,
                  createdAt: shuttleData.createdAt || null,
                  email: shuttleData.email || null,
                  telepon: shuttleData.telepon || null,
                  alamat: shuttleData.alamat || null,
                  namaLengkap: shuttleData.namaLengkap || null,
                  snapToken: shuttleData.snapToken || null,
                  orderId: shuttleData.orderId || orderId,
                });
                processedOrderIds.add(orderId);
              }
            }
          }
        } catch {}

        try {
          const suratQuery = query(
            collection(db, "suratPernyataan"),
            where("userId", "==", user.uid)
          );
          const suratSnapshot = await getDocs(suratQuery);
          suratSnapshot.forEach((doc) => {
            const data = doc.data();
            const eventId = data.eventId;
            if (eventId && data.userId === user.uid) {
              const existing = suratData[eventId];
              if (
                !existing ||
                (existing.createdAt &&
                  data.createdAt &&
                  data.createdAt.seconds > existing.createdAt.seconds)
              ) {
                suratData[eventId] = {
                  downloadURL: data.downloadURL,
                  fileName: data.fileName,
                  namaLengkap: data.namaLengkap,
                  createdAt: data.createdAt,
                };
              }
            }
          });
        } catch {}

        if (isMounted.current) {
          setPesanan(pesananList);
          setSuratPernyataan(suratData);
          setMembershipStatus(membershipData);
          setEventTitles(eventTitlesData);
          cachePesananData(user.uid, {
            pesanan: pesananList,
            suratPernyataan: suratData,
            membershipStatus: membershipData,
            eventTitles: eventTitlesData,
          });
        }
      } catch (err) {
        if (isMounted.current) {
          setError(
            err.code === "permission-denied"
              ? "Akses ditolak. Pastikan Anda memiliki izin untuk mengakses data."
              : err.code === "unavailable"
              ? "Koneksi ke server gagal. Periksa jaringan Anda."
              : "Gagal memuat data pesanan. Silakan coba lagi."
          );
          showNotification(
            "error",
            err.code === "permission-denied"
              ? "Akses ditolak. Pastikan Anda memiliki izin."
              : err.code === "unavailable"
              ? "Koneksi gagal. Periksa jaringan Anda."
              : "Gagal memuat data pesanan."
          );
        }
      } finally {
        if (isMounted.current) setLoading(false);
        fetchStatus.current.isFetching = false;
      }
    },
    [user, showNotification]
  );

  const fetchTransaction = useCallback(async () => {
    if (!orderId || fetchStatus.current.isFetching) return;
    fetchStatus.current.isFetching = true;
    try {
      const res = await axios.get(
        `${import.meta.env.VITE_API_BASE_URL}/api/payment/status/${orderId}`
      );
      if (res.data && isMounted.current) {
        console.log("Transaction data:", {
          orderId,
          transaction_time: res.data.transaction_time,
          createdAt: res.data.createdAt,
        });
        setTransaction(res.data);
      }
    } catch (error) {
      console.error("Gagal mengambil status transaksi:", error);
      if (isMounted.current) {
        setError(
          error.response?.status === 403
            ? "Akses ke API ditolak. Periksa konfigurasi server."
            : error.response?.status === 404
            ? "Order ID tidak ditemukan."
            : "Gagal mengambil data transaksi."
        );
        showNotification(
          "error",
          error.response?.status === 403
            ? "Akses ke API ditolak."
            : error.response?.status === 404
            ? "Order ID tidak ditemukan."
            : "Gagal mengambil data transaksi."
        );
      }
    } finally {
      fetchStatus.current.isFetching = false;
    }
  }, [orderId, showNotification]);

  const handleBayar = useCallback(
    (snapToken) => {
      if (snapToken && window.snap) {
        window.snap.pay(snapToken, {
          onSuccess: () => {
            showNotification("success", "Pembayaran berhasil!");
            fetchPesananDanSurat(true);
          },
          onPending: () => {
            showNotification("warning", "Silakan lanjutkan pembayaran.");
            fetchPesananDanSurat(true);
          },
          onError: () => {
            setError("Terjadi kesalahan saat pembayaran.");
            showNotification("error", "Terjadi kesalahan saat pembayaran.");
          },
          onClose: () => {},
        });
      } else {
        setError("Token pembayaran atau Midtrans tidak ditemukan.");
        showNotification(
          "error",
          "Token pembayaran atau Midtrans tidak ditemukan."
        );
      }
    },
    [showNotification, fetchPesananDanSurat]
  );

  const handleRenewMembership = useCallback(async () => {
    if (!membershipStatus || !membershipStatus.memberId || !user) {
      showNotification("error", "Data keanggotaan tidak ditemukan.");
      return;
    }
    try {
      console.log("Memulai perpanjangan:", {
        memberId: membershipStatus.memberId,
        uid: user.uid,
      });
      const idToken = await user.getIdToken(true);
      const response = await axios.post(
        `${import.meta.env.VITE_API_BASE_URL}/api/renew-membership`,
        {
          memberId: membershipStatus.memberId,
          harga: 50000,
          uid: user.uid,
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const { snapToken } = response.data;
      showNotification("success", "Proses perpanjangan dimulai.");
      handleBayar(snapToken);
    } catch (error) {
      console.error("Gagal memulai perpanjangan:", error);
      showNotification(
        "error",
        "Gagal memulai perpanjangan: " +
          (error.response?.data?.message || error.message)
      );
    }
  }, [membershipStatus, user, showNotification, handleBayar]);

  useEffect(() => {
    isMounted.current = true;
    if (!user) {
      navigate("/login");
      showNotification("error", "Silakan login untuk melihat pesanan.");
      return;
    }

    fetchPesananDanSurat(true);

    if (status && !notificationShown) {
      if (status === "settlement") {
        showNotification("success", "Pembayaran berhasil!");
        setNotificationShown(true);
      } else if (status === "failed") {
        showNotification("error", "Pembayaran gagal.");
        setNotificationShown(true);
      }
    }

    if (orderId) {
      fetchTransaction();
    }

    return () => {
      isMounted.current = false;
    };
  }, [user, navigate, orderId, status, showNotification, fetchTransaction]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredPesanan = useMemo(() => {
    if (!Array.isArray(pesanan)) {
      console.warn("pesanan bukan array:", pesanan);
      return [];
    }
    return pesanan.filter((item) => {
      if (activeMenu === "Semua Pesanan")
        return item.collectionName !== "membershipRenewals";
      if (activeMenu === "Menunggu")
        return (
          item.transaction_status === "pending" &&
          item.collectionName !== "membershipRenewals"
        );
      if (activeMenu === "Selesai")
        return (
          item.transaction_status === "settlement" &&
          item.collectionName !== "membershipRenewals"
        );
      return true;
    });
  }, [pesanan, activeMenu]);

  const renewalPesanan = useMemo(() => {
    if (!Array.isArray(pesanan)) {
      console.warn("pesanan bukan array:", pesanan);
      return [];
    }
    return pesanan.filter(
      (item) => item.collectionName === "membershipRenewals"
    );
  }, [pesanan]);

  const getEventName = (item) => {
    if (item.type === "shuttle") {
      return `${
        eventTitles[item.eventId] || item.eventId || "Tidak diketahui"
      } - Shuttle`;
    }
    return eventTitles[item.eventId] || item.eventId || "Tidak diketahui";
  };

  return (
    <section className="min-h-screen bg-slate-100 px-4 py-20">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="w-full md:w-64 bg-white rounded-xl border border-slate-200 p-4 block">
            <ul>
              {menus.map((menu) => (
                <li key={menu.name}>
                  <button
                    onClick={() => {
                      setActiveMenu(menu.name);
                      setViewMode("pesanan");
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-2 mb-1 text-md font-medium rounded-xl transition-colors duration-200 ${
                      activeMenu === menu.name && viewMode === "pesanan"
                        ? "bg-cyan-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <FontAwesomeIcon icon={menu.icon} />
                    {menu.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex-1">
            {viewMode === "membership" ? (
              <div>
                <div className="bg-white rounded-xl border border-slate-200 py-4 px-0 mb-4">
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-2 px-4">
                    <h2 className="text-lg md:text-xl font-semibold text-slate-800">
                      Detail Keanggotaan
                    </h2>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setViewMode("pesanan")}
                        className={`px-4 py-2 rounded-xl text-sm font-medium ${
                          viewMode === "pesanan"
                            ? "bg-cyan-600 text-white"
                            : "bg-gray-300 text-slate-800 hover:bg-gray-400"
                        } transition-colors duration-200`}
                      >
                        Pesanan
                      </button>
                      <button
                        onClick={() => setViewMode("membership")}
                        className={`px-4 py-2 rounded-xl text-sm font-medium ${
                          viewMode === "membership"
                            ? "bg-cyan-600 text-white"
                            : "bg-gray-300 text-slate-800 hover:bg-gray-400"
                        } transition-colors duration-200`}
                      >
                        Membership
                      </button>
                    </div>
                  </div>
                  {membershipStatus ? (
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto divide-y divide-gray-200">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Field
                            </th>
                            <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Nilai
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-4 text-sm text-gray-600">
                              ID Anggota
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">
                              {membershipStatus.memberId}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-4 text-sm text-gray-600">
                              Nama Lengkap
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">
                              {membershipStatus.namaLengkap}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-4 text-sm text-gray-600">
                              Status Keanggotaan
                            </td>
                            <td
                              className={`px-4 py-4 text-sm ${
                                membershipStatus.isActive
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              {membershipStatus.isActive
                                ? "Aktif"
                                : "Tidak Aktif"}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-4 text-sm text-gray-600">
                              Masa Aktif Hingga
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">
                              {formatDate(membershipStatus.activeUntil)}
                            </td>
                          </tr>
                          <tr className="border-b border-gray-200">
                            <td className="px-4 py-4 text-sm text-gray-600">
                              Status Transaksi
                            </td>
                            <td className="px-4 py-4 text-sm text-gray-600">
                              {membershipStatus.transaction_status ===
                              "settlement"
                                ? "Berhasil"
                                : membershipStatus.transaction_status ===
                                  "pending"
                                ? "Menunggu"
                                : "Tidak Diketahui"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-gray-600 my-4 mx-4">
                      Tidak ada data keanggotaan.
                    </p>
                  )}
                  {membershipStatus && !membershipStatus.isActive && (
                    <div className="p-4">
                      <div className="flex justify-center gap-4 mt-4">
                        <button
                          onClick={handleRenewMembership}
                          className="px-4 py-2 bg-cyan-600 text-white rounded-xl hover:bg-cyan-700 transition-colors duration-200"
                          disabled={loading}
                        >
                          {loading ? "Memproses..." : "Perpanjang Keanggotaan"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                <div className="bg-white rounded-xl border border-slate-200 py-4 px-0">
                  <h3 className="text-lg font-semibold text-slate-800 mb-4 px-4">
                    Riwayat Perpanjangan
                  </h3>
                  {renewalPesanan.length === 0 ? (
                    <p className="text-gray-600 my-4 px-4">
                      Belum ada riwayat perpanjangan.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full table-auto divide-y divide-gray-200">
                        <thead>
                          <tr className="bg-gray-100">
                            <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Order ID
                            </th>
                            <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Harga
                            </th>
                            <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Masa Aktif Baru
                            </th>
                            <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Aksi
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {renewalPesanan.map((item) => (
                            <tr
                              key={`${item.eventId}-${item.id}`}
                              className="border-b border-gray-200"
                            >
                              <td className="px-4 py-4 text-sm text-gray-600">
                                {item.orderId || "Tidak diketahui"}
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600">
                                Rp{" "}
                                {item.harga ? item.harga.toLocaleString() : "0"}
                              </td>
                              <td className="px-4 py-4 text-sm">
                                <span
                                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                                    item.transaction_status === "settlement"
                                      ? "bg-green-100 text-green-800"
                                      : "bg-yellow-100 text-yellow-800"
                                  }`}
                                >
                                  {item.transaction_status === "settlement"
                                    ? "Berhasil"
                                    : "Menunggu"}
                                </span>
                              </td>
                              <td className="px-4 py-4 text-sm text-gray-600">
                                {item.activeUntil
                                  ? formatDate(item.activeUntil)
                                  : "Tidak tersedia"}
                              </td>
                              <td className="px-4 py-4 text-sm">
                                <button
                                  onClick={() => handleBayar(item.snapToken)}
                                  className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap ${
                                    item.transaction_status === "settlement"
                                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                                      : "bg-cyan-600 text-white hover:bg-cyan-700"
                                  } transition-colors duration-200`}
                                  disabled={
                                    item.transaction_status === "settlement"
                                  }
                                >
                                  {item.transaction_status === "pending"
                                    ? "Bayar Sekarang"
                                    : "Selesai"}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl border border-slate-200 py-4 px-0">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-2 px-4">
                  <h2 className="text-lg md:text-xl font-semibold text-slate-800">
                    Riwayat Pesanan
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setViewMode("pesanan")}
                      className={`px-4 py-2 rounded-xl text-sm font-medium ${
                        viewMode === "pesanan"
                          ? "bg-cyan-600 text-white"
                          : "bg-gray-300 text-slate-800 hover:bg-gray-400"
                      } transition-colors duration-200`}
                    >
                      Pesanan
                    </button>
                    <button
                      onClick={() => setViewMode("membership")}
                      className={`px-4 py-2 rounded-xl text-sm font-medium ${
                        viewMode === "membership"
                          ? "bg-cyan-600 text-white"
                          : "bg-gray-300 text-slate-800 hover:bg-gray-400"
                      } transition-colors duration-200`}
                    >
                      Membership
                    </button>
                  </div>
                </div>
                <AnimatePresence>
                  {error && (
                    <div className="bg-red-100 text-red-800 p-4 rounded-xl mx-4 mb-4">
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        className="h-5 w-5 mr-2"
                      />
                      <span>{error}</span>
                    </div>
                  )}
                </AnimatePresence>
                <div className="overflow-x-auto">
                  <table className="w-full table-auto divide-y divide-gray-200">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Event
                        </th>
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          Kategori / ID
                        </th>
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Harga
                        </th>
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Surat
                        </th>
                        <th className="px-4 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Aksi
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPesanan.map((item) => (
                        <tr
                          key={`${item.eventId}-${item.id}`}
                          className="border-b border-gray-200"
                        >
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {getEventName(item)}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            {item.type === "shuttle"
                              ? `${shortenLocation(
                                  item.pickupPoint
                                )} - ${shortenLocation(item.returnPoint)}`
                              : item.eventId === "ALTI_MEMBERSHIP"
                              ? item.memberId || "Tidak diketahui"
                              : item.kategori || "-"}
                          </td>
                          <td className="px-4 py-4 text-sm">
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                item.transaction_status === "settlement"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {item.transaction_status === "settlement"
                                ? "Berhasil"
                                : "Menunggu"}
                            </span>
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-600">
                            Rp {item.harga ? item.harga.toLocaleString() : "0"}
                          </td>
                          <td className="px-4 py-4 text-sm">
                            {item.eventId !== "ALTI_MEMBERSHIP" &&
                            item.type !== "shuttle" &&
                            item.kategori &&
                            suratPernyataan[item.eventId] ? (
                              <a
                                href={suratPernyataan[item.eventId].downloadURL}
                                download={
                                  suratPernyataan[item.eventId].fileName
                                }
                                className="text-cyan-600 hover:text-cyan-900"
                              >
                                Unduh
                              </a>
                            ) : (
                              <span className="text-gray-500">-</span>
                            )}
                          </td>
                          <td className="px-4 py-4 text-sm">
                            {item.transaction_status === "pending" &&
                            item.snapToken ? (
                              <button
                                onClick={() => handleBayar(item.snapToken)}
                                className="px-4 py-2 rounded-xl text-sm font-medium bg-cyan-600 text-white hover:bg-cyan-700 transition-colors duration-200 whitespace-nowrap"
                              >
                                Bayar Sekarang
                              </button>
                            ) : (
                              <button
                                onClick={() =>
                                  navigate(
                                    `/pesanan-detail?eventId=${item.eventId}&orderId=${item.id}`
                                  )
                                }
                                className="px-4 py-2 rounded-xl text-sm font-medium bg-cyan-600 text-white hover:bg-cyan-700 transition-colors duration-200 whitespace-nowrap"
                              >
                                Lihat Detail
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
});
